import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdPopupSurface } from '@entities/ad-popup';
import { useCurrentAdPopup, useDismissAdPopup } from '@features/ad-popup-runtime';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@shared/ui';

import { AdPopupArtwork } from './AdPopupArtwork';

type Props = { surface: AdPopupSurface; enabled?: boolean };

export function AdPopupRuntime({ surface, enabled = true }: Props) {
  const { t } = useTranslation();
  const query = useCurrentAdPopup(surface, enabled);
  const dismissMutation = useDismissAdPopup();
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const item = query.data?.item ?? null;
  const visible = item !== null && item.id !== dismissedId;

  const dismiss = async () => {
    if (item === null || dismissMutation.isPending) return;
    setDismissedId(item.id);
    try {
      await dismissMutation.mutateAsync({ id: item.id, requestId: crypto.randomUUID() });
    } catch {
      setDismissedId(null);
    }
  };

  if (!enabled || item === null) return null;

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) void dismiss();
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('adPopups.runtime.title')}</DialogTitle>
          <DialogDescription>{t('adPopups.runtime.description')}</DialogDescription>
        </DialogHeader>
        <AdPopupArtwork imageUrl={item.imageUrl} link={item.link} onOpen={dismiss} />
      </DialogContent>
    </Dialog>
  );
}
